import { DialogComponent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';

/**
 * A custom element that manages a cart drawer.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
  }

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

(function () {
  const PROMO_ACTIVE = true;
  const PROMO_TEXT = 'İlk siparişe özel %15 indirim fırsatı';
  const PROMO_SUBTEXT = 'İndirim kodu için pop-up formunu doldurun!';

  function formatTRY(amount) {
    try {
      return new Intl.NumberFormat('tr-TR', {
        style: 'currency',
        currency: 'TRY'
      }).format(amount);
    } catch (e) {
      return amount.toFixed(0) + '₺';
    }
  }

  async function fetchCart() {
    const res = await fetch('/cart.js', { credentials: 'same-origin' });
    if (!res.ok) throw new Error('Cart fetch failed');
    return await res.json();
  }

  function ensurePromoNote() {
    const bar = document.querySelector('.sf-free-shipping[data-threshold]');
    if (!bar) return;

    let note = document.querySelector('.cart-promo-note');

    if (!PROMO_ACTIVE) {
      if (note) note.remove();
      return;
    }

    const html = `
      <div class="cart-promo-note__content">
        <span class="cart-promo-note__title">${PROMO_TEXT}</span>
        <span class="cart-promo-note__sub">${PROMO_SUBTEXT}</span>
      </div>
    `;

    if (!note) {
      note = document.createElement('div');
      note.className = 'cart-promo-note';
      note.innerHTML = html;
      bar.after(note);
    } else {
      note.innerHTML = html;
    }
  }

  function updateBars(cart) {
    const bars = document.querySelectorAll('.sf-free-shipping[data-threshold]');
    if (!bars.length) {
      const note = document.querySelector('.cart-promo-note');
      if (note) note.remove();
      return;
    }

    bars.forEach(bar => {
      const threshold = parseInt(bar.getAttribute('data-threshold'), 10); // kuruş
      const total = cart.total_price; // kuruş

      const textEl = bar.querySelector('[data-text]');
      const fillEl = bar.querySelector('[data-fill]');
      const trackEl = bar.querySelector('.sf-free-shipping__track');

      if (!textEl || !fillEl || !threshold) return;

      const remaining = Math.max(0, threshold - total);
      const pct = Math.max(0, Math.min(100, Math.round((total / threshold) * 100)));

      if (remaining > 0) {
        const remainingTRY = remaining / 100;
        textEl.textContent = `Ücretsiz kargoya ${formatTRY(remainingTRY)} kaldı.`;
      } else {
        textEl.textContent = 'Tebrikler ücretsiz kargo aktif 🤍';
      }

      fillEl.style.width = remaining > 0 ? pct + '%' : '100%';

      if (trackEl) {
        trackEl.setAttribute('aria-valuenow', String(remaining > 0 ? pct : 100));
      }
    });

    ensurePromoNote();
  }

  let t;
  function scheduleUpdate() {
    clearTimeout(t);
    t = setTimeout(async () => {
      try {
        const cart = await fetchCart();
        updateBars(cart);
      } catch (e) {
        // silently fail
      }
    }, 150);
  }

  document.addEventListener('DOMContentLoaded', scheduleUpdate);
  document.addEventListener('cart:updated', scheduleUpdate);
  document.addEventListener('cart:refresh', scheduleUpdate);

  document.addEventListener('click', function (e) {
    const inCartUI =
      e.target.closest &&
      e.target.closest('[data-cart-drawer], .cart-drawer, .drawer, form[action^="/cart"]');

    if (!inCartUI) return;

    const likelyCartAction = e.target.closest(
      'button[name="plus"], button[name="minus"], button[name="updates[]"], a[href*="change"], button, a'
    );

    if (likelyCartAction) scheduleUpdate();
  });

  const obs = new MutationObserver(scheduleUpdate);
  obs.observe(document.documentElement, {
    subtree: true,
    attributes: true,
    attributeFilter: ['class', 'aria-hidden', 'aria-expanded'],
  });
})();